import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import adminAPI from '../utils/api';
import ConfirmationModal from '../components/ConfirmationModal';
import BookingDetailModal from '../components/BookingDetailModal';
import CreateBookingModal from '../components/CreateBookingModal';

const Bookings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    coach_id: searchParams.get('coach_id') || '',
    location_id: searchParams.get('location_id') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    date: searchParams.get('date') || '',
    client_id: searchParams.get('client_id') || ''
  });

  const [coaches, setCoaches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: parseInt(searchParams.get('page')) || 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    count: 0
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-success-600 bg-success-100';
      case 'waitlist':
        return 'text-warning-600 bg-warning-100';
      case 'cancelled':
        return 'text-error-600 bg-error-100';
      case 'completed':
        return 'text-primary-600 bg-primary-100';
      case 'no_show':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmé';
      case 'waitlist':
        return 'Liste d\'attente';
      case 'cancelled':
        return 'Annulé';
      case 'completed':
        return 'Terminé';
      case 'no_show':
        return 'Absent';
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return CheckCircleIcon;
      case 'waitlist':
        return ClockIcon;
      case 'cancelled':
        return XCircleIcon;
      case 'completed':
        return CheckCircleIcon;
      case 'no_show':
        return XCircleIcon;
      default:
        return ClockIcon;
    }
  };

  // Fetch bookings from API
  const fetchBookings = async (page = pagination.currentPage) => {
    try {
      setLoading(true);
      const filtersWithPage = { ...filters, search: searchTerm, page };
      const data = await adminAPI.getBookings(filtersWithPage);
      console.log("Admin Bookings API response:", data);
      setBookings(data.results || []);

      // Update URL with current page
      updateURLParams(filters, searchTerm, page);

      // Update pagination info
      if (data.count !== undefined) {
        // Calculate total pages dynamically from API response
        let totalPages = 1;

        if (data.count > 0) {
          if (data.next || data.previous) {
            // Use current page results length as page size if available
            const currentPageSize = data.results ? data.results.length : 1;
            if (data.next) {
              // Not the last page, use current results length as page size
              totalPages = Math.ceil(data.count / currentPageSize);
            } else {
              // Last page, we are on the final page
              totalPages = page;
            }
          } else {
            // Only one page
            totalPages = 1;
          }
        }

        setPagination({
          currentPage: page,
          totalPages: totalPages,
          hasNext: !!data.next,
          hasPrevious: !!data.previous,
          count: data.count
        });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des réservations');
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [coachesData, locationsData, clientsData] = await Promise.all([
          adminAPI.getCoaches(),
          adminAPI.getLocations(),
          adminAPI.getUsers({ role: 'client' })
        ]);

        setCoaches(coachesData.results || coachesData || []);
        setLocations(locationsData.results || locationsData || []);
        setClients(clientsData.results || clientsData || []);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadInitialData();
    fetchBookings();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    fetchBookings(1);
  }, [filters, searchTerm]);

  // Update URL parameters
  const updateURLParams = (newFilters, newSearch, page = 1) => {
    const params = new URLSearchParams();

    if (newSearch) params.set('search', newSearch);
    if (page > 1) params.set('page', page.toString());

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        params.set(key, value);
      }
    });

    setSearchParams(params);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    updateURLParams(newFilters, searchTerm, 1); // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle search change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    updateURLParams(filters, value, 1); // Reset to page 1 when search changes
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Reset all filters
  const resetFilters = () => {
    const resetFilters = {
      status: 'all',
      coach_id: '',
      location_id: '',
      date_from: '',
      date_to: '',
      date: '',
      client_id: ''
    };
    setFilters(resetFilters);
    setSearchTerm('');
    updateURLParams(resetFilters, '', 1);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle pagination navigation
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchBookings(page);
  };

  // Handle delete booking
  const handleDeleteBooking = (bookingId, clientName) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Supprimer la réservation',
      message: `Êtes-vous sûr de vouloir supprimer la réservation de ${clientName} ? Cette action est irréversible.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await adminAPI.deleteBooking(bookingId);
          toast.success('Réservation supprimée avec succès');
          fetchBookings(pagination.currentPage);
          setConfirmationModal({ ...confirmationModal, isOpen: false });
        } catch (error) {
          toast.error('Erreur lors de la suppression de la réservation');
          console.error('Error deleting booking:', error);
        }
      }
    });
  };

  // Handle view booking details
  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  // Handle update booking status with confirmation
  const handleUpdateBookingStatus = (bookingId, newStatus, currentStatus) => {
    const booking = bookings.find(b => b.id === bookingId);
    const statusLabels = {
      'confirmed': 'Confirmé',
      'waitlist': 'Liste d\'attente',
      'cancelled': 'Annulé',
      'completed': 'Terminé',
      'no_show': 'Absent'
    };

    setConfirmationModal({
      isOpen: true,
      title: 'Confirmer le changement de statut',
      message: `Êtes-vous sûr de vouloir changer le statut de la réservation de "${statusLabels[currentStatus]}" vers "${statusLabels[newStatus]}" ?

Client: ${booking?.client?.full_name}
Séance: ${booking?.session?.title}
Date: ${booking?.session?.date} à ${booking?.session?.start_time}`,
      type: 'warning',
      onConfirm: async () => {
        try {
          await adminAPI.updateBooking(bookingId, { status: newStatus });
          toast.success('Statut de la réservation mis à jour');
          fetchBookings(pagination.currentPage);
        } catch (error) {
          const errorMessage = error.message || 'Erreur lors de la mise à jour du statut';
          toast.error(errorMessage);
          console.error('Error updating booking status:', error);
        }
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      }
    });
  };

  // Get available status options based on current status
  const getAvailableStatusOptions = (currentStatus) => {
    const allOptions = [
      { value: 'confirmed', label: 'Confirmé' },
      { value: 'waitlist', label: 'Liste d\'attente' },
      { value: 'cancelled', label: 'Annulé' },
      { value: 'completed', label: 'Terminé' },
      { value: 'no_show', label: 'Absent' }
    ];

    // Return all options except the current one
    return allOptions.filter(option => option.value !== currentStatus);
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Réservations</h2>
          <p className="text-gray-600">Gérez toutes les réservations du studio</p>
        </div>
        <button
          onClick={() => setShowCreateBookingModal(true)}
          className="mt-4 sm:mt-0 btn-primary flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouvelle réservation
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="card">
        <div className="flex flex-col space-y-4">
          {/* Top Row: Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher par client, coach ou studio..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 input-field"
              />
            </div>

            {/* Filter Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filtres
                {showFilters ? (
                  <ChevronUpIcon className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 ml-2" />
                )}
              </button>

              <button
                onClick={resetFilters}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Reset
              </button>
            </div>
          </div>

          {/* Collapsible Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <div>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="confirmed">Confirmé</option>
                  <option value="waitlist">Liste d'attente</option>
                  <option value="cancelled">Annulé</option>
                </select>
              </div>

              <div>
                <select
                  value={filters.coach_id}
                  onChange={(e) => handleFilterChange('coach_id', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Tous les coachs</option>
                  {coaches.map((coach) => (
                    <option key={coach.id} value={coach.id}>
                      {coach.user?.full_name || coach.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <select
                  value={filters.location_id}
                  onChange={(e) => handleFilterChange('location_id', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Tous les studios</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="input-field w-full"
                  placeholder="Date de début"
                />
              </div>

              <div>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="input-field w-full"
                  placeholder="Date de fin"
                />
              </div>

              <div>
                <select
                  value={filters.client_id}
                  onChange={(e) => handleFilterChange('client_id', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Tous les clients</option>
                  {clients.slice(0, 50).map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.full_name || client.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bookings Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Heure
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coach & Studio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Chargement...</p>
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Aucune réservation trouvée
                  </td>
                </tr>
              ) : bookings.map((booking) => {
                const StatusIcon = getStatusIcon(booking.status);
                return (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {booking.client_service?.client?.full_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {booking.client_service?.client?.email || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CalendarDaysIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm text-gray-900">
                            {booking.session?.date ? new Date(booking.session.date).toLocaleDateString('fr-FR') : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {booking.session?.start_time} - {booking.session?.end_time}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900 flex items-center">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-1" />
                          {booking.session?.coach?.user?.full_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                          {booking.session?.location?.name || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {booking.client_service?.service_type?.name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {booking.client_service?.service_type?.type === 'pack' ? 'Pack' : 'Abonnement'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {getStatusText(booking.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewDetails(booking)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>

                        {/* Status update dropdown */}
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              handleUpdateBookingStatus(booking.id, e.target.value, booking.status);
                            }
                          }}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                          title="Changer le statut"
                        >
                          <option value="">Changer statut</option>
                          {getAvailableStatusOptions(booking.status).map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>

                        <button
                          onClick={() => handleDeleteBooking(
                            booking.id,
                            booking.client_service?.client?.user?.full_name || booking.client?.full_name || 'Client'
                          )}
                          className="text-gray-400 hover:text-error-600 p-1"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{pagination.currentPage}</span> sur{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                  {pagination.count && (
                    <span className="ml-2 text-gray-500">
                      ({pagination.count} élément{pagination.count > 1 ? 's' : ''} au total)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Précédent</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {(() => {
                    const { currentPage, totalPages } = pagination;
                    const pages = [];
                    const maxVisiblePages = 5;

                    if (totalPages <= maxVisiblePages) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Always show first page
                      pages.push(1);

                      if (currentPage > 3) {
                        pages.push('...');
                      }

                      // Show pages around current page
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);

                      for (let i = start; i <= end; i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(i);
                        }
                      }

                      if (currentPage < totalPages - 2) {
                        pages.push('...');
                      }

                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => (
                      page === '...' ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ));
                  })()}

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Suivant</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-primary-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary-600">{bookings.filter(b => b.status === 'confirmed').length}</div>
          <div className="text-sm text-primary-600">Confirmées</div>
        </div>
        <div className="bg-warning-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-warning-600">{bookings.filter(b => b.status === 'waitlist').length}</div>
          <div className="text-sm text-warning-600">Liste d'attente</div>
        </div>
        <div className="bg-error-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-error-600">{bookings.filter(b => b.status === 'cancelled').length}</div>
          <div className="text-sm text-error-600">Annulées</div>
        </div>
        <div className="bg-success-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-success-600">{pagination.count || 0}</div>
          <div className="text-sm text-success-600">Total</div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />

      <BookingDetailModal
        booking={selectedBooking}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
      />

      <CreateBookingModal
        isOpen={showCreateBookingModal}
        onClose={() => setShowCreateBookingModal(false)}
        onSuccess={() => {
          setShowCreateBookingModal(false);
          fetchBookings(pagination.currentPage);
        }}
      />
    </div>
  );
};

export default Bookings;
